import { T } from "@/lib/i18n";
import { LangSelect } from "@/components/LangSelect";
import { InstallAppButton } from "@/components/InstallAppButton";
import { useChatUnread } from "@/components/chat/ClientChat";
import { useState, useEffect } from "react";
import { ChevronDown, Shield, LogOut, User as UserIcon, LayoutDashboard, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/constants/testIds";
import { useLang } from "@/context/LangContext";
import { NotificationBell } from "@/components/NotificationBell";

export const Header = ({ lang, setLang, t }) => {
  const { user, logout } = useAuth();
  const { tr } = useLang();
  const chatUnread = useChatUnread(!!user && user.role !== "super_admin");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const close = () => setMenuOpen(false);
    if (menuOpen) {
      window.addEventListener("click", close);
      return () => window.removeEventListener("click", close);
    }
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-40 w-full bg-[#E6EAEC]/90 backdrop-blur-md border-b border-[#cfd6d9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
          <img src="/brand/aurora-mark.webp" alt="AURORA-ADV logo · marketing digitale Ticino" className="w-10 h-10 shrink-0 object-cover rounded-md" />
          <div className="min-w-0">
            <div className="font-display text-base sm:text-lg leading-none tracking-tight whitespace-nowrap">
              Aurora<span className="aurora-text">-ADV</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717a] font-mono mt-1 flex items-center gap-1.5">
              <SwissCross className="w-2.5 h-2.5" />
              <span className="hidden sm:inline">{t ? t("tagline") : "Made in Switzerland"}</span>
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <InstallAppButton className="md:hidden" />
          {setLang && (
            <LangSelect lang={lang} setLang={setLang} />
          )}

          {!user ? (
            <>
              <Link
                data-testid={TID.loginLink}
                to="/login"
                className="hidden sm:inline text-sm text-[#52525b] hover:text-[#09090b] px-3"
              >
                {tr("Accedi", "Log in")}
              </Link>
              <Link to="/register">
                <Button size="sm" className="aurora-gradient text-white rounded-sm h-9 font-medium">
                  {tr("Registrati", "Sign up")}
                </Button>
              </Link>
            </>
          ) : (
            <>
            <Link data-testid="my-campaigns-link" to="/account" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[#52525b] hover:text-[#09090b] px-2">
              <LayoutDashboard className="w-4 h-4" /> {tr("Le mie campagne", "My campaigns")}
            </Link>
            {user.role !== "super_admin" && (
              <Link data-testid="header-chat-link" to="/account#chat" title={tr("Messaggi", "Messages")}
                className="relative w-9 h-9 border border-[#e4e4e7] rounded-sm flex items-center justify-center hover:bg-[#f4f4f5]">
                <MessageCircle className="w-4 h-4" />
                {chatUnread > 0 && <span data-testid="header-chat-unread" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 aurora-gradient text-white text-[10px] font-bold flex items-center justify-center rounded-full">{chatUnread}</span>}
              </Link>
            )}
            <NotificationBell />
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                data-testid={TID.userMenu}
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 px-3 h-9 border border-[#e4e4e7] rounded-sm hover:bg-[#f4f4f5]"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <span className="w-6 h-6 rounded-full aurora-gradient flex items-center justify-center text-[10px] text-white font-bold">
                    {(user.name || user.email).slice(0,1).toUpperCase()}
                  </span>
                )}
                <span className="hidden md:inline text-sm font-medium">{user.name || user.email}</span>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-11 w-56 bg-white border border-[#e4e4e7] shadow-lg py-1 z-50">
                  <div className="px-3 py-2 border-b border-[#f4f4f5]">
                    <div className="text-xs text-[#71717a] font-mono truncate">{user.email}</div>
                    <div className="text-[10px] font-mono uppercase mt-0.5">
                      {user.role === "super_admin" ? (
                        <span className="aurora-text font-semibold">● Super Admin</span>
                      ) : (
                        <span className="text-[#10b981]">● {tr("Attivo", "Active")}</span>
                      )}
                    </div>
                  </div>
                  <Link to="/account" data-testid="menu-my-campaigns" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#f4f4f5]">
                    <LayoutDashboard className="w-3.5 h-3.5" /> {tr("Le mie campagne", "My campaigns")}
                  </Link>
                  {user.role === "super_admin" && (
                    <Link
                      data-testid={TID.adminLink}
                      to="/admin"
                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#f4f4f5]"
                    >
                      <Shield className="w-3.5 h-3.5" /> Admin Console
                    </Link>
                  )}
                  <button
                    data-testid={TID.logoutBtn}
                    onClick={logout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#ef4444] hover:bg-[#fef2f2] text-left"
                  >
                    <LogOut className="w-3.5 h-3.5" /> {tr("Esci", "Log out")}
                  </button>
                </div>
              )}
            </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export const SwissCross = ({ className = "w-3 h-3" }) => (
  <svg viewBox="0 0 32 32" className={className} aria-hidden>
    <rect width="32" height="32" fill="#DA291C" />
    <rect x="13" y="6" width="6" height="20" fill="#fff" />
    <rect x="6" y="13" width="20" height="6" fill="#fff" />
  </svg>
);
